// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { z } from 'zod';
import {
  CapitalCallSubtype,
  DistributionSubtype,
  IlliquidAssetCurrency,
  IlliquidAssetEventType,
} from '@shared/data-access/types/engine-types';

const illiquidEventDateSchema = z.string().min(1);

const illiquidEventAmountSchema = z.number().positive();

const illiquidEventDescriptionSchema = z.string().max(100).optional();

/**
 * Shared financial-event fields captured in the UI (camelCase).
 *
 * @remarks
 * Maps to wire fields via {@link buildIlliquidEventCreateBody}.
 */
export const illiquidEventBaseFormSchema = z.object({
  eventDate: illiquidEventDateSchema,

  amount: illiquidEventAmountSchema,

  currency: z.enum(IlliquidAssetCurrency),

  description: illiquidEventDescriptionSchema,
});

export type IlliquidEventBaseFormData = z.infer<typeof illiquidEventBaseFormSchema>;

export const illiquidAcquisitionEventFormSchema =
  illiquidEventBaseFormSchema.extend({
    eventType: z.literal(IlliquidAssetEventType.Acquisition),
  });

export type IlliquidAcquisitionEventFormData = z.infer<
  typeof illiquidAcquisitionEventFormSchema
>;

export const illiquidCapitalCallEventFormSchema =
  illiquidEventBaseFormSchema.extend({
    eventType: z.literal(IlliquidAssetEventType.CapitalCall),

    subtype: z.enum(CapitalCallSubtype),
  });

export type IlliquidCapitalCallEventFormData = z.infer<
  typeof illiquidCapitalCallEventFormSchema
>;

export const illiquidDistributionEventFormSchema =
  illiquidEventBaseFormSchema.extend({
    eventType: z.literal(IlliquidAssetEventType.Distribution),

    subtype: z.enum(DistributionSubtype),
  });

export type IlliquidDistributionEventFormData = z.infer<
  typeof illiquidDistributionEventFormSchema
>;

export const illiquidValuationEventFormSchema = illiquidEventBaseFormSchema.extend({
  eventType: z.literal(IlliquidAssetEventType.Valuation),
});

export type IlliquidValuationEventFormData = z.infer<
  typeof illiquidValuationEventFormSchema
>;

export const illiquidEventFormSchema = z.discriminatedUnion('eventType', [
  illiquidAcquisitionEventFormSchema,
  illiquidCapitalCallEventFormSchema,
  illiquidDistributionEventFormSchema,
  illiquidValuationEventFormSchema,
]);

export type IlliquidEventFormData = z.infer<typeof illiquidEventFormSchema>;
